import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LanguageContext } from "../../../Context/Language.Context";
import { useFeatureDefinitionsHook } from "../../../Hooks/use-context-hooks";
import { PageText } from "../../../components/Text/Text";
import { resolveFeatureDefinitionTypeFromProduct } from "../../../Utils/feature-definitions-helper";
import FEATURE_DEFINITIONS from "../../Data/FeatureDefinitions.json";
import styles from "./FeatureDefinitionText.module.css";

const getLangKey = (language) => (language === "FR" ? "fr" : "en");

const getProductTypeEn = (definition) =>
  definition.productType?.en || definition["product-type-en"] || "";

const normalizeForMatch = (value = "") =>
  String(value).toLowerCase().replace(/[™®ᴹᶜᴹᴰ]/g, "");

const isLetter = (char = "") => /[a-zA-Z]/.test(char);

const isTrademarkCharacter = (char = "") => /[™®ᴹᶜᴰ]/.test(char);

const getRawMatchEnd = (source = "", startIndex = 0, feature = "") => {
  const target = normalizeForMatch(feature);
  let rawEndIndex = startIndex;
  let collected = "";

  while (rawEndIndex < source.length && collected.length < target.length) {
    rawEndIndex += 1;
    collected = normalizeForMatch(source.slice(startIndex, rawEndIndex));
  }

  if (collected !== target) return null;

  while (
    rawEndIndex < source.length &&
    isTrademarkCharacter(source[rawEndIndex])
  ) {
    rawEndIndex += 1;
  }

  if (
    rawEndIndex < source.length &&
    source[rawEndIndex] === "s" &&
    isLetter(source[rawEndIndex - 1])
  ) {
    rawEndIndex += 1;
  }

  if (
    rawEndIndex < source.length &&
    isLetter(source[rawEndIndex]) &&
    isLetter(source[rawEndIndex - 1])
  ) {
    return null;
  }

  return rawEndIndex;
};

const buildFeatureList = ({
  definitions = [],
  langKey = "en",
  product,
  categoryRestrictive = false,
}) => {
  const resolvedProductType = resolveFeatureDefinitionTypeFromProduct(product);

  return definitions
    .map((definition) => {
      const feature =
        definition.feature?.[langKey] ||
        definition.feature?.en ||
        definition[langKey === "fr" ? "feature-fr" : "feature-en"] ||
        definition["feature-en"];

      const definitionText =
        definition.definition?.[langKey] ||
        definition.definition?.en ||
        definition[langKey === "fr" ? "definition-fr" : "definition-en"] ||
        definition["definition-en"];

      const productTypeEn = getProductTypeEn(definition);

      if (!feature || !definitionText) return null;

      if (categoryRestrictive) {
        if (!resolvedProductType) return null;
        if (productTypeEn !== resolvedProductType) return null;
      }

      return {
        feature,
        normalizedFeature: normalizeForMatch(feature),
        definitionText,
        productTypeEn,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.feature.length - a.feature.length);
};

const tokenizeText = (text = "", featureList = []) => {
  const source = String(text);
  const tokens = [];
  let index = 0;

  while (index < source.length) {
    const sourceSlice = source.slice(index);

    const match = featureList.find((item) => {
      if (!item.normalizedFeature) return false;
      return normalizeForMatch(sourceSlice).startsWith(item.normalizedFeature);
    });

    if (match) {
      const rawEndIndex = getRawMatchEnd(source, index, match.feature);

      if (rawEndIndex !== null) {
        tokens.push({
          type: "feature",
          text: source.slice(index, rawEndIndex),
          featureTitle: match.feature,
          definitionText: match.definitionText,
        });

        index = rawEndIndex;
        continue;
      }
    }

    tokens.push({ type: "text", text: source[index] });
    index += 1;
  }

  return tokens;
};

const mergeTextTokens = (tokens = []) =>
  tokens.reduce((acc, token) => {
    const previous = acc[acc.length - 1];

    if (token.type === "text" && previous?.type === "text") {
      previous.text += token.text;
      return acc;
    }

    acc.push(token);
    return acc;
  }, []);

const FeatureTooltipToken = ({
  token,
  tokenId,
  isOpen,
  setOpenTooltipId,
}) => {
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState(null);

  const openTooltip = () => {
    setOpenTooltipId(tokenId);
  };

  const closeTooltip = () => {
    setOpenTooltipId(null);
  };

  const updatePosition = () => {
    if (!wrapperRef.current || !tooltipRef.current) return;

    const gap = 10;
    const margin = 16;

    const triggerRect = wrapperRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const preferredLeft =
      triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

    const left = Math.min(
      Math.max(preferredLeft, margin),
      viewportWidth - tooltipRect.width - margin
    );

    const hasRoomAbove = triggerRect.top >= tooltipRect.height + gap + margin;

    const preferredTop = hasRoomAbove
      ? triggerRect.top - tooltipRect.height - gap
      : triggerRect.bottom + gap;

    const top = Math.min(
      Math.max(preferredTop, margin),
      viewportHeight - tooltipRect.height - margin
    );

    setPosition({ left, top });
  };

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleUpdate = () => updatePosition();

    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate, true);

    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
    };
  }, [isOpen]);

  return (
    <span
      ref={wrapperRef}
      className={styles.tooltipWrapper}
      tabIndex={0}
      role="button"
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") {
          openTooltip();
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") {
          closeTooltip();
        }
      }}
      onFocus={() => {
        openTooltip();
      }}
      onClick={(event) => {
        event.stopPropagation();
        openTooltip();
      }}
    >
      <span className={styles.featureText}>{token.text}</span>
      <span className={styles.featureInfoIcon} aria-hidden="true">
        i
      </span>

      {isOpen && (
        <span
          ref={tooltipRef}
          className={styles.tooltipBox}
          style={
            position
              ? {
                  left: `${position.left}px`,
                  top: `${position.top}px`,
                }
              : undefined
          }
        >
          <span className={styles.tooltipTitle}>{token.featureTitle}</span>
          <span className={styles.tooltipDescription}>
            {token.definitionText}
          </span>
        </span>
      )}
    </span>
  );
};

const FeatureDefinitionText = ({
  text,
  product,
  textType = "productPageSpecList",
  categoryRestrictive = false,
}) => {
  const { language } = useContext(LanguageContext);
  const { definitions } = useFeatureDefinitionsHook();
  const [openTooltipId, setOpenTooltipId] = useState(null);

  const langKey = getLangKey(language);

  const resolvedDefinitions =
    Array.isArray(definitions) && definitions.length > 0
      ? definitions
      : FEATURE_DEFINITIONS;

  const featureList = useMemo(() => {
    if (!Array.isArray(resolvedDefinitions)) return [];

    return buildFeatureList({
      definitions: resolvedDefinitions,
      langKey,
      product,
      categoryRestrictive,
    });
  }, [resolvedDefinitions, langKey, product, categoryRestrictive]);

  const tokens = useMemo(() => {
    if (!text || featureList.length === 0) {
      return [{ type: "text", text }];
    }

    return mergeTextTokens(tokenizeText(text, featureList));
  }, [text, featureList]);

  useEffect(() => {
    const handlePointerDown = () => {
      setOpenTooltipId(null);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpenTooltipId(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <PageText type={textType}>
      {tokens.map((token, index) => {
        if (token.type === "text") {
          return <span key={index}>{token.text}</span>;
        }

        const tokenId = `${token.featureTitle}-${index}`;

        return (
          <FeatureTooltipToken
            key={tokenId}
            token={token}
            tokenId={tokenId}
            isOpen={openTooltipId === tokenId}
            setOpenTooltipId={setOpenTooltipId}
          />
        );
      })}
    </PageText>
  );
};

export default FeatureDefinitionText;